import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Camera, Film, Plane, Award, Users, MapPin } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } }),
};

const STATS = [
  { value: "500+", label: "Events Captured" },
  { value: "10+", label: "Years Experience" },
  { value: "50+", label: "VIP Clients" },
  { value: "3", label: "Countries Served" },
];

const VALUES = [
  { icon: Camera, title: "Uncompromising Quality", desc: "Every frame is crafted with editorial precision. We don't shoot events — we create visual legacies." },
  { icon: Film, title: "Cinematic Vision", desc: "Our videography transforms moments into motion picture experiences that stand the test of time." },
  { icon: Plane, title: "Global Reach", desc: "From Kingston to the world. We bring Caribbean luxury to international stages." },
  { icon: Award, title: "Excellence as Standard", desc: "Award-winning photography and production that sets the benchmark for the industry." },
  { icon: Users, title: "Client-First Approach", desc: "Your vision drives everything. We listen, we plan, we deliver beyond expectations." },
  { icon: MapPin, title: "Rooted in Jamaica", desc: "Proudly Jamaican. Our work celebrates the beauty, culture, and energy of the Caribbean." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pt-20 bg-luxury-black">
      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gold rounded-full blur-[200px]" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div initial="hidden" animate="visible">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-4">About Aristocrats JA</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight mb-6 max-w-3xl">
              The Story Behind the <span className="gold-gradient-text">Lens</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/50 font-body font-light text-base md:text-lg leading-relaxed max-w-2xl">
              Founded by Matthew Taylor in Kingston, Jamaica, Aristocrats JA Signature Events was born from a singular vision: to elevate event documentation into an art form worthy of the extraordinary people we serve.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-20 md:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.div variants={fadeUp} custom={0} className="aspect-[4/5] overflow-hidden relative">
              <img
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=85"
                alt="Matthew Taylor - Founder"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8">
                <p className="text-gold font-body text-xs uppercase tracking-[0.2em] mb-1">Founder & Creative Director</p>
                <p className="text-white font-heading text-2xl">Matthew Taylor</p>
              </div>
            </motion.div>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.2em] text-gold mb-4">The Visionary</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl sm:text-4xl font-light text-white mb-6">
              A Pursuit of <span className="text-gold">Perfection</span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-white/50 font-body font-light leading-relaxed mb-6">
              With over a decade of experience capturing Jamaica's most prestigious events, Matthew Taylor has built Aristocrats JA into the island's most sought-after event documentation studio. His work combines editorial precision with artistic vision, creating images and films that transcend the ordinary.
            </motion.p>
            <motion.p variants={fadeUp} custom={3} className="text-white/50 font-body font-light leading-relaxed mb-6">
              From yacht galas in Montego Bay to corporate launches in Kingston, from beach weddings in Negril to carnival celebrations across the Caribbean — every event receives the same meticulous attention to detail and creative excellence.
            </motion.p>
            <motion.p variants={fadeUp} custom={4} className="text-white/40 font-body font-light leading-relaxed italic border-l-2 border-gold/40 pl-6">
              "We don't just capture moments — we immortalize legacies. Every client deserves nothing less than perfection, and that's exactly what we deliver."
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-t border-b border-white/5 bg-luxury-charcoal">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((stat, i) => (
              <motion.div key={stat.label} variants={fadeUp} custom={i} className="text-center">
                <p className="font-heading text-4xl md:text-5xl text-gold mb-2">{stat.value}</p>
                <p className="text-white/40 font-body text-xs uppercase tracking-[0.15em]">{stat.label}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.2em] text-gold mb-4">Our Philosophy</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-heading text-3xl sm:text-4xl font-light text-white">What Sets Us Apart</motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {VALUES.map((v, i) => (
              <motion.div key={v.title} variants={fadeUp} custom={i} className="glass-card p-8 group hover:border-gold/20 transition-colors">
                <v.icon size={28} className="text-gold/60 mb-5 group-hover:text-gold transition-colors" />
                <h3 className="text-white font-body text-sm font-medium mb-3">{v.title}</h3>
                <p className="text-white/40 font-body text-sm leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-luxury-charcoal relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold rounded-full blur-[120px]" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs font-body font-medium uppercase tracking-[0.2em] text-gold mb-4">Ready to Begin?</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-light text-white mb-6">Let's Create Something Extraordinary Together</h2>
          <p className="text-white/50 font-body font-light mb-10">Whether it's a wedding, corporate gala, or private celebration, we bring the same passion and precision to every project.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/booking" data-testid="about-cta-book" className="inline-flex items-center gap-2 bg-gold text-black font-body font-medium text-sm px-10 py-4 hover:bg-gold-light transition-colors">
              Book a Consultation <ArrowRight size={16} />
            </Link>
            <Link to="/portfolio" className="inline-flex items-center gap-2 border border-white/20 text-white font-body font-light text-sm px-10 py-4 hover:border-white/50 transition-colors">
              View Our Work
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
